import React, { useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { Axis } from 'highcharts';
import { observer } from 'mobx-react';
import { upperFirst } from 'lodash';
import xrange from 'highcharts/modules/xrange';
import boost from 'highcharts/modules/boost';
import noDataToDdisplay from 'highcharts/modules/no-data-to-display';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';
import { AlertLevels, CompressorActivityStateEnum, InsightType } from '@/constants/enums';
import IconNotification from '@/assets/icon-notification.svg?react';
import EPCollapse from '@/comp/EPCollapse';
import { CustomThemeToken } from '@/theme';
import DeviceStatusChart from './DeviceStatusChart';
import { ChartContainer, StyledCollapse } from './styled';
import DynamicController from './DynamicController';
import SpinCentered from '@/comp/SpinCentered';

const { EPPanel } = EPCollapse;

boost(Highcharts);
noDataToDdisplay(Highcharts);
xrange(Highcharts);

const { useToken } = theme;

export const getSeriesColorMapping = (token: CustomThemeToken) => ({
  [CompressorActivityStateEnum.Load]: token.colorPrimary,
  [CompressorActivityStateEnum.Unload]: token.colorText,
  [CompressorActivityStateEnum.Off]: token.colorBorder,
  'n/a': token.colorBorder,
  warning: token.colorGoldActive,
  shutdown: token.colorError,
  maintenance: token.colorWarning,
});

export const compressorAlertStatus = [
  upperFirst(AlertLevels.Warning),
  upperFirst(AlertLevels.Shutdown),
  upperFirst(InsightType.Maintenance),
];

// Workaround for Highcharts bug: not showing shared tooltip when having multiple x or y axis
(function (H) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Highcharts internal API modification
  (H as any).Pointer.prototype.getHoverData = function (
    existingHoverPoint,
    existingHoverSeries,
    series,
    isDirectTouch,
    shared,
    e,
  ) {
    let hoverSeries = existingHoverSeries;
    const hoverPoints = [],
      useExisting = !!(isDirectTouch && existingHoverPoint),
      notSticky = hoverSeries && !hoverSeries.stickyTracking,
      filter = function (s) {
        return s.visible && !(!shared && s.directTouch) && H.pick(s.options.enableMouseTracking, true);
      };

    let searchSeries = notSticky
      ? [hoverSeries]
      : series.filter(function (s) {
          return filter(s) && s.stickyTracking;
        });

    const hoverPoint = useExisting || !e ? existingHoverPoint : this.findNearestKDPoint(searchSeries, shared, e);

    hoverSeries = hoverPoint && hoverPoint.series;

    if (hoverPoint) {
      if (shared && !hoverSeries.noSharedTooltip) {
        searchSeries = series.filter(function (s) {
          return filter(s) && !s.noSharedTooltip;
        });

        searchSeries.forEach(function (s) {
          let point = H.find(s.points, function (p) {
            return p.clientX === hoverPoint.clientX && !p.isNull;
          });

          if (H.isObject(point)) {
            if (s.chart.isBoosting) {
              point = s.getPoint(point);
            }
            hoverPoints.push(point);
          }
        });
      } else {
        hoverPoints.push(hoverPoint);
      }
    }
    return {
      hoverPoint: hoverPoint,
      hoverSeries: hoverSeries,
      hoverPoints: hoverPoints,
    };
  };
})(Highcharts);

const charts = Highcharts.charts;

function syncronizeCrossHairs(token: CustomThemeToken) {
  return function innerSyncronizeCrossHairs(chart) {
    const container = chart.container;
    let x;

    container.addEventListener('mousemove', function (evt) {
      x = evt?.chartX - chart?.plotLeft;

      const xAxis = chart?.xAxis[0];
      const xVal = xAxis?.toValue(x, true);

      const firstChart = charts?.find(chart => chart?.options?.chart?.type === 'line');
      const xAxisMainChart = firstChart?.xAxis?.[0];
      const pointsMainChart = firstChart?.series?.[0]?.points;

      if (pointsMainChart?.length) {
        pointsMainChart?.forEach((point, i) => {
          if (i + 1 < pointsMainChart?.length && point.x <= xVal && pointsMainChart?.[i + 1]?.x > xVal) {
            point.setState();
            pointsMainChart?.[i + 1].setState();

            if (xVal - point.x <= pointsMainChart?.[i + 1].x - xVal) {
              firstChart?.tooltip?.refresh(point);
              point?.setState('hover');
            } else {
              firstChart?.tooltip?.refresh(pointsMainChart[i + 1]);
              pointsMainChart?.[i + 1].setState('hover');
            }
          }
        });

        xAxisMainChart.removePlotLine('myPlotLineId');
        xAxisMainChart.addPlotLine({
          value: chart?.xAxis?.[0]?.translate(x, true),
          width: 1,
          color: token.colorBorder,
          id: 'myPlotLineId',
        });

        charts?.forEach(chartArrayItem => {
          if (chartArrayItem && chartArrayItem?.options?.chart?.type === 'xrange') {
            const xAxis2 = chartArrayItem.xAxis[0];
            xAxis2.removePlotLine('myPlotLineId');
            xAxis2.addPlotLine({
              value: chart?.xAxis?.[0]?.translate(x, true),
              width: 1,
              color: token.colorBorder,
              id: 'myPlotLineId',
            });

            Highcharts.each(chartArrayItem.series?.[0]?.points, function (point) {
              if (point?.x < xVal && point?.x2 > xVal) {
                chartArrayItem.tooltip?.refresh(point);
              }
            });
          }
        });
      }
    });
  };
}

const resizeCharts = () => {
  const firstxMainAxis = charts?.find(chart => chart?.options?.chart?.type === 'line')?.xAxis?.[0] as Axis & {
    len: number;
  };
  charts?.forEach(chart => {
    if (chart && chart?.options?.chart?.type === 'xrange') {
      chart?.update({ chart: { width: firstxMainAxis?.len } }, true);
      chart.reflow();
    }
  });
};

const DynamicControllerChart = observer(() => {
  const { investigationStore } = useStores();
  const { t } = useTranslation();
  const { token } = useToken();

  useEffect(() => {
    resizeCharts();
  }, []);

  return (
    <ChartContainer $isDynamicControl>
      <div className="charts">
        <DynamicController
          syncronizeCrossHairs={syncronizeCrossHairs(token as CustomThemeToken)}
          highcharts={Highcharts}
        />
        {investigationStore.isCompareMode && !!investigationStore.proactiveEngineLogsSeries2?.length && (
          <DynamicController
            syncronizeCrossHairs={syncronizeCrossHairs(token as CustomThemeToken)}
            highcharts={Highcharts}
            isSecondPeriod
          />
        )}
      </div>
      {!!(
        investigationStore.proactiveEngineLogsSeries?.length || investigationStore.proactiveEngineLogsSeries2?.length
      ) && (
        <div className="legend">
          <div className="legend-item">
            <div className="legend-square" style={{ backgroundColor: token.colorSuccess as string }} />
            {t('on')}
          </div>
          <div className="legend-item">
            <div className="legend-square" style={{ backgroundColor: token.colorError as string }} />
            {t('off')}
          </div>
        </div>
      )}
    </ChartContainer>
  );
});

const DevicesStatusCollapse = observer(() => {
  const { investigationStore } = useStores();
  const { t } = useTranslation();
  const { token } = useToken();

  useEffect(() => {
    resizeCharts();
  }, []);

  return (
    <StyledCollapse key="devices-status-containers">
      <EPPanel header={t('devicesStatus')} key="devices-status" forceRender>
        <ChartContainer>
          <div className="charts">
            {investigationStore.devicesMetrics.map(
              deviceMetric =>
                deviceMetric.isSelectedInDeviceStatus && (
                  <div key={deviceMetric?.id}>
                    <DeviceStatusChart
                      series={investigationStore.getFilteredDevicesStatusSeries(deviceMetric?.id)}
                      data={investigationStore.deviceStatusSeries}
                      compressorName={deviceMetric?.name}
                      key={deviceMetric?.id}
                      syncronizeCrossHairs={syncronizeCrossHairs(token as CustomThemeToken)}
                      highcharts={Highcharts}
                    />
                    {investigationStore.getFilteredDevicesStatusSeries(deviceMetric?.id, true)?.length > 0 && (
                      <DeviceStatusChart
                        series={investigationStore.getFilteredDevicesStatusSeries(deviceMetric?.id, true)}
                        data={investigationStore.deviceStatusSeries}
                        key={`${deviceMetric?.id}-alerts`}
                        highcharts={Highcharts}
                      />
                    )}
                  </div>
                ),
            )}
          </div>
          <div className="legend">
            {Object.entries(getSeriesColorMapping(token as CustomThemeToken)).map(([key, value]) => (
              <div className="legend-item" key={key}>
                <div className="legend-square" style={{ backgroundColor: value as string }} />
                {upperFirst(t(key))}
              </div>
            ))}
          </div>
        </ChartContainer>
      </EPPanel>
    </StyledCollapse>
  );
});

const Graph = observer(({ width, options, setChart, isZoomed, t }) => {
  const { investigationStore, permissionStore } = useStores();

  const hasEnhancedIDViewPermission = permissionStore.isAuth(permissions.VIEW_ENHANCED_INVESTIGATION_DASHBOARD);

  Highcharts.setOptions({
    lang: {
      resetZoom: t('Reset zoom'),
    },
  });

  return (
    <div className="graph-container">
      <div className="graph-view" style={{ width }}>
        {investigationStore.isLoading && <SpinCentered className="loader" />}
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            ...options,
            ...(hasEnhancedIDViewPermission && {
              chart: {
                ...options.chart,
                events: {
                  redraw: resizeCharts,
                },
              },
              xAxis: [
                ...(options.xAxis ?? []).map(xAxis => ({
                  ...xAxis,
                  events: {
                    afterSetExtremes: function (e) {
                      if (e?.trigger === 'zoom') {
                        charts?.forEach(chart => {
                          if (chart && chart?.options?.chart?.type === 'xrange') {
                            chart.xAxis[0].setExtremes(this.chart.xAxis[0].min, this.chart.xAxis[0].max);
                          }
                        });
                      }
                    },
                  },
                })),
              ],
            }),
          }}
          callback={setChart}
        />
        {hasEnhancedIDViewPermission && <DynamicControllerChart />}
        {hasEnhancedIDViewPermission &&
          investigationStore.isAnyCompressorSelectedInDeviceStatus &&
          investigationStore.dates1 &&
          !investigationStore.isCompareMode &&
          investigationStore.deviceStatusSeries?.length > 0 && <DevicesStatusCollapse />}
        <div className="explainer">
          <IconNotification className="icon-explainer" />
          {isZoomed ? t('Alt + Left mouse to pan') : t('Left mouse to zoom')}
        </div>
      </div>
    </div>
  );
});

export default Graph;
